import React, { useEffect, useMemo, useState } from "react";
import { Checkbox, debounce } from "@mui/material";
import { translate } from "../../../../../../common/providers";
import {
	CustomTable,
	CustomTableBody,
	CustomTableCell,
	CustomTableHead,
	CustomTableRow,
	OptionsPanelContainer,
	SearchInput,
	SidePanel,
	SidePanelContent,
} from "../../../../../../common/components";
import { useApi } from "../../../../../../common/hooks";
import { AiAgentsService } from "../../../../../../api";
import styles from "./AgentToolSidePanel.module.css";
import ToolRow from "./ToolRow";
import { isNonEmptyArray } from "../../../../../../common/utils";

const debounceFunc = debounce((func) => func(), 500);
export default function AgentToolSidePanel({ agentId, agentName, open, onClose, onRefresh }) {
	const [agentTools, setAgentTools] = useState([]);
	const [isSaving, setIsSaving] = useState(false);
	const [searchInput, setSearchInput] = useState("");
	const [currentSelectedTools, setCurrentSelectedTools] = useState([]);
	const headers = useMemo(
		() => [
			"",
			translate("dsi.agents.tool.property.name"),
			translate("dsi.agents.tool.property.description"),
			translate("dsi.agents.tool.property.input"),
			translate("dsi.agents.tool.property.output"),
			translate("dsi.agents.tool.property.writes-in-db"),
		],
		[]
	);
	const allChecked = useMemo(
		() => agentTools.every((tool) => currentSelectedTools.includes(tool.id)),
		[agentTools, currentSelectedTools]
	);
	const indeterminateAllChecked = useMemo(
		() => agentTools.some((tool) => currentSelectedTools.includes(tool.id)),
		[agentTools, currentSelectedTools]
	);
	const { call: getAgentTools } = useApi(AiAgentsService.getAgentTools);
	const { call: updateAgentTools } = useApi(AiAgentsService.updateAgentTools);
	useEffect(() => {
		if (open && agentId) {
			getAgentTools({ agentId })
				.then((data) => {
					const { selectedTools, unselectedTools } = data;
					setCurrentSelectedTools(selectedTools.map((tool) => tool.id));
					setAgentTools([
						...selectedTools.map((tool) => ({ ...tool })),
						...unselectedTools.map((tool) => ({ ...tool })),
					]);
				})
				.catch(console.error);
		}
	}, [open, agentId, getAgentTools]);
	const sortTools = (toolsToSort) =>
		toolsToSort.sort((a, b) => {
			const aPriority = currentSelectedTools.includes(a.id);
			const bPriority = currentSelectedTools.includes(b.id);
			if (aPriority && !bPriority) {
				return -1;
			}
			if (!aPriority && bPriority) {
				return 1;
			}
			return a.methodName.localeCompare(b.methodName);
		});
	const getAgentToolsWithSearch = (filter) => {
		debounceFunc(() => {
			getAgentTools({ agentId, filter })
				.then((data) => {
					const { selectedTools, unselectedTools } = data;
					setAgentTools(sortTools([...selectedTools, ...unselectedTools]));
				})
				.catch(console.error);
		});
	};
	const handleCheckTool = (id) => {
		if (currentSelectedTools.includes(id)) {
			setCurrentSelectedTools((prev) => prev.filter((toolId) => toolId !== id));
		} else {
			setCurrentSelectedTools((prev) => [...prev, id]);
		}
	};
	const handleCheckAll = () => {
		if (allChecked) {
			setCurrentSelectedTools((prev) =>
				prev.filter((toolId) => !agentTools.map((tool) => tool.id).includes(toolId))
			);
		} else {
			setCurrentSelectedTools((prev) => [...new Set([...prev, ...agentTools.map((tool) => tool.id)])]);
		}
		setAgentTools((prev) => prev.map((tool) => ({ ...tool, checked: !allChecked })));
	};
	const handleClose = () => {
		if (onClose && typeof onClose === "function") {
			onClose();
			setSearchInput("");
			setCurrentSelectedTools([]);
		}
	};
	const handleChangeSearch = (e) => {
		setSearchInput(e.target.value);
		getAgentToolsWithSearch(e.target.value);
	};
	const handleClearSearch = () => {
		setSearchInput("");
		getAgentToolsWithSearch("");
	};
	const handleSubmit = () => {
		setIsSaving(true);
		updateAgentTools({ agentId }, { toolIds: currentSelectedTools })
			.then(() => {
				setTimeout(() => {
					setIsSaving(false);
				}, 300);
				onRefresh();
			})
			.catch(console.error);
	};
	const rowRenderer = (tool) => (
		<ToolRow
			key={tool.id}
			isChecked={currentSelectedTools.includes(tool.id)}
			isSaving={isSaving}
			tool={tool}
			onCheckTool={handleCheckTool}
		/>
	);
	return (
		<SidePanel disableBackdropClick open={open} size={90} onClose={handleClose}>
			<SidePanelContent
				className={styles.content}
				title={translate("dsi.agents.tools.update.side-panel.title", { agentName })}
				onClose={handleClose}
			>
				<OptionsPanelContainer
					buttonText={(isSaving && translate("common:btn.saving")) || translate("common:btn.save")}
					disabledPrimaryButton={isSaving}
					title={translate("dsi.agents.tools")}
					onClick={handleSubmit}
				>
					<div className={styles.search}>
						<SearchInput
							placeholder={translate("common:btn.search")}
							value={searchInput}
							onChange={handleChangeSearch}
							onClearSearch={handleClearSearch}
						/>
					</div>
					<CustomTable dense>
						<CustomTableHead>
							<CustomTableRow className={styles.header}>
								{headers.map((header) => (
									<CustomTableCell key={header || "action"}>
										{header || (
											<Checkbox
												checked={allChecked}
												disabled={isSaving}
												indeterminate={!allChecked && indeterminateAllChecked}
												onChange={handleCheckAll}
											/>
										)}
									</CustomTableCell>
								))}
							</CustomTableRow>
						</CustomTableHead>
						<CustomTableBody>
							{isNonEmptyArray(agentTools) && agentTools.map((row) => rowRenderer(row))}
						</CustomTableBody>
					</CustomTable>
				</OptionsPanelContainer>
			</SidePanelContent>
		</SidePanel>
	);
}
